import { api } from './api';
import type {
  ConnectStatusResponse,
  OnboardExpertRequest,
  OnboardExpertResponse,
  StripeCustomer,
  CreateCustomerRequest,
  PaymentMethod,
  AddPaymentMethodRequest,
  EscrowHold,
  FundEscrowRequest,
  ReleaseEscrowRequest,
  EscrowListResponse,
  ExpertBalance,
  Payout,
  PayoutListResponse,
  RequestPayoutRequest,
  Transaction,
  TransactionListParams,
  TransactionListResponse,
} from '../types/payment';

// ============================================================================
// Expert Connect Account
// ============================================================================

/**
 * Start Stripe Connect onboarding for an expert
 */
export async function onboardExpert(
  data: OnboardExpertRequest
): Promise<OnboardExpertResponse> {
  const response = await api.post('/payments/connect/onboard', data);
  return response.data;
}

/**
 * Get Connect account status
 */
export async function getConnectStatus(): Promise<ConnectStatusResponse> {
  const response = await api.get('/payments/connect/status');
  return response.data;
}

/**
 * Refresh onboarding link
 */
export async function refreshOnboardingLink(): Promise<OnboardExpertResponse> {
  const response = await api.post('/payments/connect/refresh');
  return response.data;
}

/**
 * Get Stripe Express dashboard link
 */
export async function getDashboardLink(): Promise<{ dashboard_url: string }> {
  const response = await api.get('/payments/connect/dashboard');
  return response.data;
}

// ============================================================================
// Company Customer
// ============================================================================

/**
 * Create or get Stripe customer for company
 */
export async function createCustomer(
  data: CreateCustomerRequest
): Promise<StripeCustomer> {
  const response = await api.post('/payments/customer', data);
  return response.data;
}

/**
 * Get company's Stripe customer
 */
export async function getCustomer(): Promise<StripeCustomer> {
  const response = await api.get('/payments/customer');
  return response.data;
}

// ============================================================================
// Payment Methods
// ============================================================================

/**
 * Add a payment method
 */
export async function addPaymentMethod(
  data: AddPaymentMethodRequest
): Promise<PaymentMethod> {
  const response = await api.post('/payments/methods', data);
  return response.data;
}

/**
 * List payment methods
 */
export async function listPaymentMethods(): Promise<PaymentMethod[]> {
  const response = await api.get('/payments/methods');
  return response.data;
}

/**
 * Delete a payment method
 */
export async function deletePaymentMethod(id: string): Promise<void> {
  await api.delete(`/payments/methods/${id}`);
}

/**
 * Set default payment method
 */
export async function setDefaultPaymentMethod(id: string): Promise<void> {
  await api.put(`/payments/methods/${id}/default`);
}

// ============================================================================
// Escrow
// ============================================================================

/**
 * Fund an escrow
 */
export async function fundEscrow(data: FundEscrowRequest): Promise<EscrowHold> {
  const response = await api.post('/payments/escrow', data);
  return response.data;
}

/**
 * Get escrow details
 */
export async function getEscrow(id: string): Promise<EscrowHold> {
  const response = await api.get(`/payments/escrow/${id}`);
  return response.data;
}

/**
 * Release escrow funds to expert
 */
export async function releaseEscrow(
  id: string,
  data?: ReleaseEscrowRequest
): Promise<EscrowHold> {
  const response = await api.post(`/payments/escrow/${id}/release`, data || {});
  return response.data;
}

/**
 * Refund escrow to company
 */
export async function refundEscrow(id: string): Promise<EscrowHold> {
  const response = await api.post(`/payments/escrow/${id}/refund`);
  return response.data;
}

/**
 * List company escrows
 */
export async function listCompanyEscrows(
  page: number = 1,
  pageSize: number = 20
): Promise<EscrowListResponse> {
  const response = await api.get('/payments/escrow', {
    params: { page, page_size: pageSize },
  });
  return response.data;
}

/**
 * List expert escrows
 */
export async function listExpertEscrows(
  page: number = 1,
  pageSize: number = 20
): Promise<EscrowListResponse> {
  const response = await api.get('/payments/expert/escrows', {
    params: { page, page_size: pageSize },
  });
  return response.data;
}

// ============================================================================
// Expert Balance & Payouts
// ============================================================================

/**
 * Get expert balance
 */
export async function getExpertBalance(): Promise<ExpertBalance> {
  const response = await api.get('/payments/balance');
  return response.data;
}

/**
 * Request a payout
 */
export async function requestPayout(data: RequestPayoutRequest): Promise<Payout> {
  const response = await api.post('/payments/withdraw', data);
  return response.data;
}

/**
 * List expert payouts
 */
export async function listPayouts(
  page: number = 1,
  pageSize: number = 20
): Promise<PayoutListResponse> {
  const response = await api.get('/payments/payouts', {
    params: { page, page_size: pageSize },
  });
  return response.data;
}

// ============================================================================
// Transactions
// ============================================================================

/**
 * List transactions
 */
export async function listTransactions(
  params: TransactionListParams = {}
): Promise<TransactionListResponse> {
  const response = await api.get('/payments/transactions', {
    params: {
      page: params.page || 1,
      page_size: params.page_size || 20,
      type: params.type,
      status: params.status,
    },
  });
  return response.data;
}

/**
 * Get a single transaction
 */
export async function getTransaction(id: string): Promise<Transaction> {
  const response = await api.get(`/payments/transactions/${id}`);
  return response.data;
}
